import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Download, Loader2, PenTool, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { PortalFirmarDialog } from '@/components/portal/PortalFirmarDialog';

export default function PortalInformes() {
  const [informes, setInformes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [firmando, setFirmando] = useState<any | null>(null);

  async function cargar() {
    const { data } = await (supabase as any)
      .from('informes_consolidados')
      .select('*')
      .or('archivado.is.null,archivado.eq.false')
      .order('created_at', { ascending: false });
    setInformes((data ?? []) as any[]);
    setLoading(false);
  }

  function categoriaLegible(cat: string | null): string {
    if (!cat) return 'Informe de revisión';
    const map: Record<string, string> = {
      mantenimiento_trimestral: 'Informe revisión trimestral',
      mantenimiento_semestral: 'Informe revisión semestral',
      mantenimiento_anual: 'Informe revisión anual',
      revision_inicial: 'Informe revisión inicial',
      intervencion: 'Informe de intervención',
    };
    return map[cat] || ('Informe ' + cat.replace(/_/g, ' '));
  }

  function tituloInforme(i: any): string {
    const base = categoriaLegible(i.categoria);
    const fecha = i.fecha || i.created_at;
    if (!fecha) return base;
    return `${base} · ${format(new Date(fecha), 'MM-yyyy')}`;
  }

  useEffect(() => {
    cargar();
  }, []);

  async function descargar(path: string | null) {
    if (!path) return toast.error('PDF no disponible');
    const { data, error } = await supabase.storage
      .from('informes-consolidados')
      .createSignedUrl(path, 60);
    if (error || !data) return toast.error('No se pudo descargar');
    window.open(data.signedUrl, '_blank');
  }

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">Informes</h1>
        <p className="text-muted-foreground">Informes de mantenimiento e intervenciones</p>
      </div>

      {informes.length === 0 && (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No tienes informes todavía.</CardContent></Card>
      )}

      <div className="grid gap-2">
        {informes.map(i => (
          <Card key={i.id}>
            <CardContent className="py-4 flex items-center gap-3 flex-wrap">
              <FileText className="h-8 w-8 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{tituloInforme(i)}</p>
                <p className="text-xs text-muted-foreground">
                  {(i.fecha || i.created_at) && format(new Date(i.fecha || i.created_at), "d 'de' MMMM yyyy", { locale: es })}
                  {i.id && <span className="ml-2 opacity-60">· Ref. {String(i.id).slice(0, 8)}</span>}
                </p>
                {i.firma_cliente && (
                  <Badge variant="outline" className="mt-1 bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Firmado{i.nombre_firmante_cliente ? ` por ${i.nombre_firmante_cliente}` : ''}
                  </Badge>
                )}
              </div>
              <div className="flex gap-2">
                {!i.firma_cliente && i.estado === 'finalizado' && (
                  <Button size="sm" onClick={() => setFirmando(i)}>
                    <PenTool className="h-4 w-4 mr-2" />
                    Firmar
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => descargar(i.pdf_path || i.archivo_path)}>
                  <Download className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {firmando && (
        <PortalFirmarDialog
          open={!!firmando}
          onOpenChange={(o) => !o && setFirmando(null)}
          tabla="informes_consolidados"
          documentoId={firmando.id}
          numeroDocumento={tituloInforme(firmando)}
          tituloDocumento="informe"
          onFirmado={cargar}
        />
      )}
    </div>
  );
}
